/**
 * Finster Demo brand style configuration — colors, typography, and prompt
 * rules extracted from "default.pptx" (slide master colorScheme
 * "Bank of SJ" + fontScheme "R&Co 2022", layout placeholder geometry, via
 * the OOXML).
 *
 * Mirrors the shape of `maFinancialStyleConfig.ts` / `messierStyleConfig.ts`.
 * When Finster Demo style is active the prompt below is appended to the
 * system prompt and the color/theme constants are used as renderer defaults.
 *
 * Fidelity note: colors come from the master colorScheme "Bank of SJ"; fonts
 * from fontScheme "R&Co 2022" (Georgia Pro for titles, Arial for body). Slide
 * size is 780×540 pt. Title/content geometry is from the slide master. The
 * logo lives on the slide master, so default AddSlide inherits it.
 */

import type { ThemeColors } from "../../types";

// ---------------------------------------------------------------------------
// Color palette (6-digit hex, no '#') — from the "Bank of SJ" colorScheme
// ---------------------------------------------------------------------------

export const FINSTER_DEMO_COLORS = {
  ink: "111111", // dk1 — titles & body text
  navy: "102A43", // dk2
  cream: "F5F2EA", // lt2 — Background2
  deepNavy: "042355", // accent1 — primary brand / emphasis
  gold: "BA9952", // accent2
  teal: "28666E", // accent3
  slate: "6A7F9B", // accent4
  sand: "D8C49A", // accent5
  brick: "9B4A50", // accent6
  linkBlue: "2F6FA3", // hlink
  followedLink: "765875", // folHlink

  white: "FFFFFF", // lt1
  ruleGray: "6A7F9B", // rule / separator lines (accent4 slate)
  altRow: "F5F2EA", // light cream for alternate-row table shading
} as const;

/**
 * Drop-in ThemeColors override when Finster Demo style is active. Values map
 * 1:1 to the slide-master colorScheme + fontScheme.
 */
export const FINSTER_DEMO_THEME_COLORS: ThemeColors = {
  dark1: FINSTER_DEMO_COLORS.ink, // #111111 — Text1
  dark2: FINSTER_DEMO_COLORS.navy, // #102A43 — Text2
  light1: FINSTER_DEMO_COLORS.white, // #FFFFFF — Background1
  light2: FINSTER_DEMO_COLORS.cream, // #F5F2EA — Background2
  accent1: FINSTER_DEMO_COLORS.deepNavy, // #042355
  accent2: FINSTER_DEMO_COLORS.gold, // #BA9952
  accent3: FINSTER_DEMO_COLORS.teal, // #28666E
  accent4: FINSTER_DEMO_COLORS.slate, // #6A7F9B
  accent5: FINSTER_DEMO_COLORS.sand, // #D8C49A
  accent6: FINSTER_DEMO_COLORS.brick, // #9B4A50
  hyperlink: FINSTER_DEMO_COLORS.linkBlue,
  followedHyperlink: FINSTER_DEMO_COLORS.followedLink,
  majorFont: "Georgia Pro",
  minorFont: "Arial",
};

/**
 * Ordered series palette. Led by deep navy (accent1), then gold / teal /
 * slate accents for readable multi-series separation.
 */
export const FINSTER_DEMO_CHART_SERIES_PALETTE = [
  FINSTER_DEMO_COLORS.deepNavy,
  FINSTER_DEMO_COLORS.gold,
  FINSTER_DEMO_COLORS.teal,
  FINSTER_DEMO_COLORS.slate,
  FINSTER_DEMO_COLORS.brick,
  FINSTER_DEMO_COLORS.sand,
];

// ---------------------------------------------------------------------------
// Slide dimensions & placeholder positions (from the Finster Demo master)
// ---------------------------------------------------------------------------

export const FINSTER_DEMO_SLIDE_DIMENSIONS = { width: 780, height: 540 } as const;

export const FINSTER_DEMO_POSITIONS = {
  title: { left: 47.0, top: 36.2, width: 686.0, height: 25.3 },
  contentStart: { left: 47.0, top: 114.1 },
  footnote: { left: 47.0, top: 510.0, width: 686.0, height: 18.0 },
} as const;

// Authoritative layout names from the Finster Demo master.
export const FINSTER_DEMO_LAYOUT_NAMES = [
  "Title and Text",
  "Title and Text, Notes Column",
  "One Box",
  "One Box, Notes Column",
  "Two Boxes (horizontal)",
  "Two Boxes (vertical)",
  "Four Boxes (vertical)",
  "Divider_Green Grad",
  "DEFAULT",
] as const;

// ---------------------------------------------------------------------------
// Prompt text — appended to the system prompt when Finster Demo style is active
// ---------------------------------------------------------------------------

export const FINSTER_DEMO_STYLE_PROMPT = `

## FINSTER DEMO BRAND STYLE OVERRIDE (active — apply these rules to ALL output)

CRITICAL FONT RULE: You MUST include \`fontFamily\` on EVERY shape you create or modify — even when editing existing text. This OVERRIDES the general rule about matching the existing presentation's fonts. Finster Demo uses:
- Titles / headings: \`"Georgia Pro"\`
- Body, bullets, tables, charts, footnotes: \`"Arial"\`
NEVER omit fontFamily when Finster Demo style is active. If a shape already has the wrong font, include fontFamily to correct it.

### Color palette (from the master colorScheme "Bank of SJ")
Use ONLY these Finster Demo brand colors. Do NOT use arbitrary hex values.

**Theme mapping:**
- Text1 / dark1: Ink #${FINSTER_DEMO_COLORS.ink}
- Text2 / dark2: Navy #${FINSTER_DEMO_COLORS.navy}
- Background1 / light1: White #${FINSTER_DEMO_COLORS.white}
- Background2 / light2: Cream #${FINSTER_DEMO_COLORS.cream}
- Accent1: Deep navy #${FINSTER_DEMO_COLORS.deepNavy}
- Accent2: Gold #${FINSTER_DEMO_COLORS.gold}
- Accent3: Teal #${FINSTER_DEMO_COLORS.teal}
- Accent4: Slate #${FINSTER_DEMO_COLORS.slate}
- Accent5: Sand #${FINSTER_DEMO_COLORS.sand}
- Accent6: Brick #${FINSTER_DEMO_COLORS.brick}

**Usage:**
- **Primary brand / emphasis**: Deep navy #${FINSTER_DEMO_COLORS.deepNavy}
- **Text / ink**: #${FINSTER_DEMO_COLORS.ink}
- **Lines & borders**: Slate #${FINSTER_DEMO_COLORS.ruleGray}
- **Background / shading**: Cream #${FINSTER_DEMO_COLORS.altRow}
- **Chart series palette** (in order): ${FINSTER_DEMO_CHART_SERIES_PALETTE.map((c) => `#${c}`).join(", ")}
  Lead with deep navy (#${FINSTER_DEMO_COLORS.deepNavy}) as the first/primary series color.

### Typography (authoritative — from the Finster Demo master)
Slide dimensions: 780 × 540 pt.

**Placeholder positions:**
- **Title**: left=47.0, top=36.2, width=686.0, height=25.3 — "Georgia Pro", deep navy (#${FINSTER_DEMO_COLORS.deepNavy}) or ink, left-aligned
- **Content area**: starts at left=47.0, top=114.1
- **Footer / page number**: bottom band around top=510 — "Arial" ~9pt, slate

**Content text hierarchy (from the master — "Arial", ink #${FINSTER_DEMO_COLORS.ink}, ~10pt body):**
- Keep body text ~10pt; sub-levels smaller. Sentence case, single-spaced, left-justified.
- For an emphasized section heading, use "Georgia Pro" or the deep-navy accent; bullets otherwise inherit ink text.

### Charts (Finster Demo style)
- Font: "Arial", minimum 8pt
- **Primary data color**: deep navy (#${FINSTER_DEMO_COLORS.deepNavy}); multi-series uses the series palette above in order
- Data labels: positioned outside bars/columns (inside for stacked)
- Gridlines: minimal — none by default; only when no data labels are shown
- Legend: bottom-center placement (only when needed for multi-series)
- No 3D effects, gradients, or graphic fills

### Tables (Finster Demo style)
- Header row: deep navy (#${FINSTER_DEMO_COLORS.deepNavy}) fill with white text, bold
- Body: white fill, ink "Arial" text (10pt)
- Alternate-row shading: cream (#${FINSTER_DEMO_COLORS.altRow})
- Borders: thin slate (#${FINSTER_DEMO_COLORS.ruleGray}); align numeric columns consistently

### Shapes & layout (Finster Demo style)
- Separate content with SHARP rectangular blocks — NOT rounded shapes
- Use thin slate (#${FINSTER_DEMO_COLORS.ruleGray}) rule lines for separation when needed
- Connectors: solid slate (#${FINSTER_DEMO_COLORS.ruleGray}) lines
- Page numbers / footer: ~9pt, slate, "Arial"

REFERENCE PRECEDENTS: The attached "default.pptx" contains the current visual standard. Match the closest relevant precedent for company overviews, financials, process pages, and comparison layouts. Prefer its title hierarchy, spacing, and footer treatment over inventing a generic layout.

**TEXT FITTING RULE**: Keep labels and item descriptions SHORT (max 6-8 words each). Text boxes are narrow; long descriptions overflow. Use concise labels, not full sentences.
`;
